// High-yield USMLE Step 1 daily facts
// Covers: Biochemistry, Physiology, Anatomy, Microbiology, Immunology, Pharmacology, Pathology

export const usmle1Facts = [
  // --- BIOCHEMISTRY ---
  {
    fact: "Pyruvate kinase deficiency is the most common cause of hereditary hemolytic anemia due to an enzyme defect (after G6PD deficiency).",
    category: "Biochemistry",
  },
  {
    fact: "G6PD deficiency protects against Plasmodium falciparum malaria and is X-linked recessive. Heinz bodies form from oxidized, denatured hemoglobin.",
    category: "Biochemistry",
  },
  {
    fact: "The rate-limiting step of glycolysis is phosphofructokinase-1 (PFK-1), activated by AMP and fructose-2,6-bisphosphate, inhibited by ATP and citrate.",
    category: "Biochemistry",
  },
  {
    fact: "Acetyl-CoA cannot be converted to glucose in humans — it enters the TCA cycle or is used for fatty acid/ketone body synthesis.",
    category: "Biochemistry",
  },
  {
    fact: "Biotin is the cofactor for carboxylases: pyruvate carboxylase, acetyl-CoA carboxylase, propionyl-CoA carboxylase, and methylcrotonyl-CoA carboxylase.",
    category: "Biochemistry",
  },
  {
    fact: "The urea cycle takes place partly in the mitochondria and partly in the cytosol. The first two steps (carbamoyl phosphate synthesis and citrulline formation) occur in the mitochondria.",
    category: "Biochemistry",
  },
  {
    fact: "Familial hypercholesterolemia (Type IIa) is caused by a defect in the LDL receptor, leading to very high LDL and premature atherosclerosis.",
    category: "Biochemistry",
  },
  {
    fact: "Phenylketonuria (PKU) is caused by phenylalanine hydroxylase deficiency. It presents with intellectual disability, musty odor, and fair complexion.",
    category: "Biochemistry",
  },
  {
    fact: "Maple syrup urine disease is caused by a deficiency of branched-chain α-ketoacid dehydrogenase, leading to accumulation of leucine, isoleucine, and valine.",
    category: "Biochemistry",
  },
  {
    fact: "HbA1c reflects average blood glucose over the past 2–3 months (lifespan of a red blood cell). It is formed by non-enzymatic glycation of hemoglobin.",
    category: "Biochemistry",
  },
  {
    fact: "The Km is the substrate concentration at which reaction velocity is half of Vmax. A high Km means low affinity; a low Km means high affinity.",
    category: "Biochemistry",
  },
  {
    fact: "Competitive inhibitors increase apparent Km but do not change Vmax. Noncompetitive inhibitors decrease Vmax but do not change Km.",
    category: "Biochemistry",
  },
  {
    fact: "Lesch-Nyhan syndrome is caused by HGPRT deficiency (X-linked). It presents with hyperuricemia, gout, self-mutilation, and choreoathetosis.",
    category: "Biochemistry",
  },
  {
    fact: "Von Gierke disease (Type I glycogen storage disease) is caused by glucose-6-phosphatase deficiency. It presents with severe fasting hypoglycemia and hepatomegaly.",
    category: "Biochemistry",
  },
  {
    fact: "Pompe disease (Type II glycogen storage disease) is caused by lysosomal α-1,4-glucosidase (acid maltase) deficiency. It causes cardiomegaly and is fatal in infancy.",
    category: "Biochemistry",
  },
  {
    fact: "Cori disease (Type III) lacks the debranching enzyme α-1,6-glucosidase. McArdle disease (Type V) lacks muscle glycogen phosphorylase — causing painful cramps with exercise.",
    category: "Biochemistry",
  },
  {
    fact: "Niemann-Pick disease is caused by sphingomyelinase deficiency. It presents with hepatosplenomegaly, 'cherry-red' spot on the macula, and progressive neurodegeneration.",
    category: "Biochemistry",
  },
  {
    fact: "Gaucher disease (most common lysosomal storage disease) is caused by glucocerebrosidase deficiency. Gaucher cells have a 'crumpled tissue paper' appearance.",
    category: "Biochemistry",
  },
  {
    fact: "Tay-Sachs disease is caused by hexosaminidase A deficiency, leading to GM2 ganglioside accumulation. It presents with cherry-red spot and no hepatosplenomegaly.",
    category: "Biochemistry",
  },
  {
    fact: "Fabry disease is caused by α-galactosidase A deficiency (X-linked). It presents with angiokeratomas, peripheral neuropathy, and renal/cardiac disease.",
    category: "Biochemistry",
  },
  {
    fact: "Homocystinuria can be caused by cystathionine synthase deficiency or methionine synthase deficiency. It presents with lens dislocation (upward and outward), DVT/stroke risk, and tall marfanoid stature.",
    category: "Biochemistry",
  },
  {
    fact: "Ornithine transcarbamylase (OTC) deficiency is the most common urea cycle disorder. It is X-linked and causes hyperammonemia after protein intake.",
    category: "Biochemistry",
  },

  // --- PHYSIOLOGY ---
  {
    fact: "The Frank-Starling law states that increased preload (end-diastolic volume) increases stroke volume, up to a point.",
    category: "Physiology",
  },
  {
    fact: "Cardiac output = heart rate × stroke volume. Stroke volume is determined by preload, afterload, and contractility.",
    category: "Physiology",
  },
  {
    fact: "The P wave represents atrial depolarization. The QRS complex represents ventricular depolarization. The T wave represents ventricular repolarization.",
    category: "Physiology",
  },
  {
    fact: "Aldosterone acts on the principal cells of the collecting duct to increase Na+ reabsorption and K+ excretion. It is regulated by angiotensin II and hyperkalemia.",
    category: "Physiology",
  },
  {
    fact: "ADH (vasopressin) increases water reabsorption in the collecting duct by inserting aquaporin-2 channels. It is released in response to increased plasma osmolality or decreased blood volume.",
    category: "Physiology",
  },
  {
    fact: "In metabolic acidosis, the body compensates with hyperventilation (Kussmaul breathing) to blow off CO2 and raise pH.",
    category: "Physiology",
  },
  {
    fact: "The Henderson-Hasselbalch equation: pH = pKa + log([HCO3−] / [0.03 × pCO2]). Normal arterial pH is 7.35–7.45.",
    category: "Physiology",
  },
  {
    fact: "GFR can be estimated by creatinine clearance. Normal GFR is ~120 mL/min. GFR decreases with age (~1 mL/min/year after age 40).",
    category: "Physiology",
  },
  {
    fact: "Surfactant (dipalmitoylphosphatidylcholine, DPPC) is produced by type II pneumocytes. It reduces surface tension and prevents alveolar collapse.",
    category: "Physiology",
  },
  {
    fact: "The A-a gradient = PAO2 − PaO2. A normal A-a gradient is < 10–15 mmHg. An elevated gradient indicates a V/Q mismatch, diffusion defect, or shunt.",
    category: "Physiology",
  },
  {
    fact: "Erythropoietin (EPO) is produced by peritubular fibroblasts of the kidney in response to hypoxia. It stimulates RBC production in the bone marrow.",
    category: "Physiology",
  },
  {
    fact: "IGF-1 (somatomedin C) mediates most anabolic effects of GH. GH is released in pulses during sleep and stimulates IGF-1 from the liver.",
    category: "Physiology",
  },
  {
    fact: "Renin is released by juxtaglomerular cells in response to decreased renal perfusion, decreased NaCl at the macula densa, and β1 adrenergic stimulation.",
    category: "Physiology",
  },
  {
    fact: "The renal threshold for glucose is ~180 mg/dL. Above this, glucose appears in urine (glucosuria), as seen in diabetes mellitus.",
    category: "Physiology",
  },
  {
    fact: "In a right-to-left shunt, the alveolar PO2 is normal but arterial PO2 falls. 100% O2 does NOT correct hypoxemia in a shunt.",
    category: "Physiology",
  },
  {
    fact: "Increased intracranial pressure causes Cushing's triad: hypertension, bradycardia, and irregular respirations.",
    category: "Physiology",
  },

  // --- ANATOMY ---
  {
    fact: "The femoral triangle contains (from lateral to medial): the femoral nerve, femoral artery, and femoral vein. The mnemonic is NAVY (lateral to medial): Nerve, Artery, Vein, Y-fronts (empty space).",
    category: "Anatomy",
  },
  {
    fact: "The inguinal canal transmits the spermatic cord in males and the round ligament of the uterus in females. Its deep ring lies lateral to the inferior epigastric vessels.",
    category: "Anatomy",
  },
  {
    fact: "The recurrent laryngeal nerve (branch of CN X) is at risk during thyroid surgery. It supplies all intrinsic laryngeal muscles except the cricothyroid (which is supplied by the external branch of the superior laryngeal nerve).",
    category: "Anatomy",
  },
  {
    fact: "The brachial plexus is formed from ventral rami of C5–T1. Erb's palsy (C5–C6) presents with 'waiter's tip' posture. Klumpke's palsy (C8–T1) presents with claw hand.",
    category: "Anatomy",
  },
  {
    fact: "The radial nerve (C5–T1) innervates the extensors of the arm and forearm. Injury causes wrist drop and loss of grip strength.",
    category: "Anatomy",
  },
  {
    fact: "The ulnar nerve (C8–T1) passes posterior to the medial epicondyle. Injury causes 'claw hand,' loss of hypothenar muscles, and sensory loss in the medial 1.5 fingers.",
    category: "Anatomy",
  },
  {
    fact: "The median nerve passes through the carpal tunnel. Carpal tunnel syndrome causes sensory loss in the lateral 3.5 fingers and thenar muscle wasting ('ape hand').",
    category: "Anatomy",
  },
  {
    fact: "The diaphragm has three major openings: T8 (IVC + right phrenic nerve), T10 (esophagus + vagus nerves), T12 (aorta + thoracic duct + azygos vein). Mnemonic: I 8 (ate) 10 Eggs At 12.",
    category: "Anatomy",
  },
  {
    fact: "The portal triad consists of a portal arteriole, portal venule, and bile ductule. Blood flows from the portal triad (zone 1) to the central vein (zone 3).",
    category: "Anatomy",
  },
  {
    fact: "The coronary arteries fill during diastole. The left anterior descending (LAD) artery supplies the anterior wall and anterior 2/3 of the interventricular septum.",
    category: "Anatomy",
  },
  {
    fact: "McBurney's point (2/3 of the way from the umbilicus to the anterior superior iliac spine) is the site of maximal tenderness in appendicitis.",
    category: "Anatomy",
  },
  {
    fact: "The ureter passes under the uterine artery ('water under the bridge') and is at risk of ligation during hysterectomy.",
    category: "Anatomy",
  },
  {
    fact: "The pudendal nerve (S2–S4) is anesthetized for perineal procedures. 'S2,3,4 keeps the pee off the floor.'",
    category: "Anatomy",
  },

  // --- MICROBIOLOGY ---
  {
    fact: "Staphylococcus aureus produces protein A, which binds the Fc region of IgG and prevents opsonization and phagocytosis.",
    category: "Microbiology",
  },
  {
    fact: "Streptococcus pneumoniae is the most common cause of pneumonia, meningitis, and otitis media in adults. It is optochin-sensitive and bile-soluble.",
    category: "Microbiology",
  },
  {
    fact: "Neisseria gonorrhoeae and N. meningitidis both ferment glucose. Only N. gonorrhoeae lacks a polysaccharide capsule. Gonorrhea is the most common reportable STI in the US.",
    category: "Microbiology",
  },
  {
    fact: "Clostridium difficile causes pseudomembranous colitis. It is associated with antibiotic use (especially clindamycin, ampicillin, fluoroquinolones). Treat with oral vancomycin or fidaxomicin.",
    category: "Microbiology",
  },
  {
    fact: "Bordetella pertussis causes whooping cough. Its virulence factors include pertussis toxin (increases cAMP via Gi inhibition) and filamentous hemagglutinin.",
    category: "Microbiology",
  },
  {
    fact: "Mycobacterium tuberculosis is an acid-fast bacillus. Primary TB causes Ghon complex (subpleural granuloma + hilar lymph node). Reactivation TB affects the upper lobes.",
    category: "Microbiology",
  },
  {
    fact: "Herpes simplex virus (HSV-1) causes oral herpes and temporal lobe encephalitis. HSV-2 causes genital herpes and neonatal herpes. Both establish latency in sensory ganglia.",
    category: "Microbiology",
  },
  {
    fact: "EBV (HHV-4) causes infectious mononucleosis. It infects B cells via CD21. Diagnosis is confirmed by heterophile antibody test (Monospot). Amoxicillin causes a diffuse maculopapular rash in mono.",
    category: "Microbiology",
  },
  {
    fact: "HIV is an RNA retrovirus with reverse transcriptase. It infects CD4+ T cells via gp120 binding to CD4 and a co-receptor (CCR5 for M-tropic, CXCR4 for T-tropic strains).",
    category: "Microbiology",
  },
  {
    fact: "Hepatitis B has a partially double-stranded DNA genome with a DNA polymerase that has reverse transcriptase activity. HBsAg appears first; anti-HBs indicates immunity/recovery.",
    category: "Microbiology",
  },
  {
    fact: "Listeria monocytogenes is a gram-positive rod that causes meningitis in neonates, pregnant women, elderly, and immunocompromised patients. It is transmitted via unpasteurized dairy and deli meats.",
    category: "Microbiology",
  },
  {
    fact: "Aspergillus fumigatus causes invasive aspergillosis in immunocompromised patients. It forms V-shaped (45°) branching hyphae. The 'halo sign' on CT is characteristic.",
    category: "Microbiology",
  },
  {
    fact: "Cryptococcus neoformans causes meningitis in immunocompromised patients (especially AIDS). India ink stain shows a clear halo (capsule). Antigen detected by latex agglutination.",
    category: "Microbiology",
  },
  {
    fact: "Plasmodium falciparum is the most lethal malaria species. It infects all RBC ages and causes cerebral malaria. It does not form hypnozoites (no liver dormancy).",
    category: "Microbiology",
  },

  // --- IMMUNOLOGY ---
  {
    fact: "MHC class I (HLA-A, B, C) presents endogenous antigens to CD8+ cytotoxic T cells. All nucleated cells express MHC I. The mnemonic is '8 × 1 = 8' (CD8 recognizes MHC I).",
    category: "Immunology",
  },
  {
    fact: "MHC class II (HLA-DR, DP, DQ) presents exogenous antigens to CD4+ helper T cells. Only APCs (dendritic cells, macrophages, B cells) express MHC II.",
    category: "Immunology",
  },
  {
    fact: "DiGeorge syndrome (22q11 deletion) causes T cell deficiency due to thymic aplasia. It presents with tetany (hypocalcemia), cardiac defects, and recurrent infections.",
    category: "Immunology",
  },
  {
    fact: "Bruton's agammaglobulinemia is X-linked and caused by BTK kinase deficiency. B cells fail to mature; patient has no immunoglobulins and presents after 6 months of age (maternal IgG wanes).",
    category: "Immunology",
  },
  {
    fact: "IgA deficiency is the most common primary immunodeficiency. Patients have recurrent sinopulmonary infections and cannot receive IgA-containing blood products (anaphylaxis risk).",
    category: "Immunology",
  },
  {
    fact: "The complement system: C3b is the main opsonin. C5a is a neutrophil chemoattractant. C5b–C9 form the membrane attack complex (MAC).",
    category: "Immunology",
  },
  {
    fact: "Type I hypersensitivity (immediate) is IgE-mediated and involves mast cells/basophils (e.g., anaphylaxis, asthma, atopy). Tested with skin prick test.",
    category: "Immunology",
  },
  {
    fact: "Type II hypersensitivity is antibody-mediated cytotoxicity (IgG/IgM). Examples: autoimmune hemolytic anemia, Goodpasture syndrome, myasthenia gravis, Graves' disease.",
    category: "Immunology",
  },
  {
    fact: "Type III hypersensitivity involves immune complex deposition. Examples: SLE, serum sickness, Arthus reaction, post-streptococcal glomerulonephritis.",
    category: "Immunology",
  },
  {
    fact: "Type IV hypersensitivity is T cell-mediated (delayed-type). Examples: contact dermatitis (poison ivy), PPD test (TB skin test), transplant rejection, and MS.",
    category: "Immunology",
  },
  {
    fact: "Regulatory T cells (Tregs) express CD4, CD25, and FoxP3. They suppress immune responses and are essential for self-tolerance. IPEX syndrome results from FoxP3 mutations.",
    category: "Immunology",
  },

  // --- PHARMACOLOGY ---
  {
    fact: "Beta-1 adrenergic receptors are in the heart (increase HR and contractility). Beta-2 receptors are in the lungs and peripheral vasculature (bronchodilation and vasodilation).",
    category: "Pharmacology",
  },
  {
    fact: "ACE inhibitors (e.g., lisinopril) block conversion of angiotensin I to II. Side effects: dry cough (due to bradykinin accumulation) and angioedema. Contraindicated in pregnancy.",
    category: "Pharmacology",
  },
  {
    fact: "Statins (HMG-CoA reductase inhibitors) are first-line for hypercholesterolemia. Side effects: myopathy (especially with concurrent fibrates) and hepatotoxicity.",
    category: "Pharmacology",
  },
  {
    fact: "Warfarin inhibits vitamin K epoxide reductase, reducing synthesis of factors II, VII, IX, X and proteins C and S. It is monitored with PT/INR and is reversed by vitamin K or FFP.",
    category: "Pharmacology",
  },
  {
    fact: "Heparin activates antithrombin III, which inhibits thrombin (factor IIa) and factor Xa. Monitored with aPTT. Reversed by protamine sulfate.",
    category: "Pharmacology",
  },
  {
    fact: "Metformin (first-line for type 2 DM) inhibits mitochondrial complex I, reducing hepatic gluconeogenesis. It does not cause hypoglycemia or weight gain. Contraindicated in renal failure (risk of lactic acidosis).",
    category: "Pharmacology",
  },
  {
    fact: "Penicillins inhibit bacterial cell wall synthesis by binding to penicillin-binding proteins (transpeptidases), blocking peptidoglycan cross-linking. They are bactericidal.",
    category: "Pharmacology",
  },
  {
    fact: "Aminoglycosides (gentamicin, tobramycin) inhibit the 30S ribosomal subunit. They are bactericidal. Major side effects are nephrotoxicity and ototoxicity (especially with loop diuretics).",
    category: "Pharmacology",
  },
  {
    fact: "Chloramphenicol inhibits the 50S ribosomal subunit (peptidyl transferase). It can cause aplastic anemia and 'gray baby syndrome' (due to immature liver glucuronidation).",
    category: "Pharmacology",
  },
  {
    fact: "Fluconazole inhibits fungal CYP450 (14α-demethylase), blocking ergosterol synthesis. It is used for Candida and Cryptococcus infections.",
    category: "Pharmacology",
  },
  {
    fact: "Lithium is used for bipolar disorder. It has a very narrow therapeutic index. Side effects: nephrogenic diabetes insipidus, hypothyroidism, and Ebstein's anomaly (if taken in pregnancy).",
    category: "Pharmacology",
  },
  {
    fact: "SSRIs are first-line for depression, OCD, panic disorder, and PTSD. Serotonin syndrome (hyperthermia, clonus, agitation) can occur especially with MAOIs.",
    category: "Pharmacology",
  },
  {
    fact: "Acetaminophen overdose causes hepatotoxicity via accumulation of NAPQI (a toxic metabolite). Treat with N-acetylcysteine (replenishes glutathione).",
    category: "Pharmacology",
  },

  // --- PATHOLOGY ---
  {
    fact: "Virchow's triad for thrombosis: endothelial damage, stasis of blood flow, and hypercoagulability.",
    category: "Pathology",
  },
  {
    fact: "The four hallmarks of inflammation are rubor (redness), calor (heat), tumor (swelling), and dolor (pain). Functio laesa (loss of function) is sometimes added as the fifth.",
    category: "Pathology",
  },
  {
    fact: "Granulomatous inflammation involves activated macrophages (epithelioid cells) and multinucleated giant cells. Causes: TB, sarcoidosis, Crohn's disease, fungal infections, foreign body reaction.",
    category: "Pathology",
  },
  {
    fact: "Reed-Sternberg cells (owl-eye appearance with prominent nucleoli) are the pathognomonic cells of Hodgkin's lymphoma. They are CD15+ and CD30+.",
    category: "Pathology",
  },
  {
    fact: "The Philadelphia chromosome (t(9;22)) produces the BCR-ABL fusion protein with constitutive tyrosine kinase activity. It is found in CML and some ALL cases.",
    category: "Pathology",
  },
  {
    fact: "p53 (tumor suppressor gene) is mutated in >50% of human cancers. Li-Fraumeni syndrome is caused by germline p53 mutations and leads to multiple cancers at an early age.",
    category: "Pathology",
  },
  {
    fact: "Caseating granulomas are seen in tuberculosis and histoplasmosis. Non-caseating granulomas are seen in sarcoidosis, Crohn's disease, and berylliosis.",
    category: "Pathology",
  },
  {
    fact: "Mallory bodies (intracytoplasmic eosinophilic inclusions of damaged intermediate filaments) are seen in alcoholic hepatitis and NASH.",
    category: "Pathology",
  },
  {
    fact: "In myocardial infarction, the earliest change visible on light microscopy is coagulative necrosis with wavy fibers at 1–3 days. Neutrophil infiltration peaks at day 3–5.",
    category: "Pathology",
  },
  {
    fact: "Kartagener syndrome (primary ciliary dyskinesia) is caused by defective dynein arms, leading to immotile cilia. It presents with situs inversus, bronchiectasis, and infertility.",
    category: "Pathology",
  },
  {
    fact: "BRCA1 and BRCA2 mutations increase the risk of breast and ovarian cancer. BRCA1 is also linked to triple-negative breast cancer.",
    category: "Pathology",
  },
  {
    fact: "α-1-antitrypsin deficiency causes panacinar emphysema (lower lobes) and liver disease (PAS-positive inclusions in hepatocytes). It is the most common inherited liver disease in children.",
    category: "Pathology",
  },
  {
    fact: "Psammoma bodies (laminated concentric calcifications) are found in papillary thyroid carcinoma, meningioma, serous papillary cystadenocarcinoma of the ovary, and mesothelioma.",
    category: "Pathology",
  },
  {
    fact: "Rouleaux formation (RBCs stacking like coins) is seen in multiple myeloma and other states of elevated protein (ESR is elevated). It is not true agglutination.",
    category: "Pathology",
  },

  // --- GENETICS ---
  {
    fact: "Autosomal dominant diseases: Marfan syndrome, Huntington's disease, familial hypercholesterolemia, neurofibromatosis (NF1, NF2), ADPKD, hereditary spherocytosis.",
    category: "Genetics",
  },
  {
    fact: "Autosomal recessive diseases: cystic fibrosis, sickle cell disease, PKU, galactosemia, glycogen storage diseases, hemochromatosis, Wilson's disease.",
    category: "Genetics",
  },
  {
    fact: "X-linked recessive diseases primarily affect males: Duchenne/Becker muscular dystrophy, hemophilia A and B, G6PD deficiency, Fabry disease, Bruton's agammaglobulinemia.",
    category: "Genetics",
  },
  {
    fact: "Cystic fibrosis (ΔF508 mutation most common) causes defective CFTR chloride channel. Results in thick mucus, recurrent pulmonary infections, pancreatic exocrine insufficiency, and infertility in males (absent vas deferens).",
    category: "Genetics",
  },
  {
    fact: "Huntington's disease is autosomal dominant with anticipation (expanding CAG repeats on chromosome 4). It causes progressive chorea, dementia, and psychiatric symptoms. Onset typically in the 40s.",
    category: "Genetics",
  },
  {
    fact: "Down syndrome (trisomy 21) features: intellectual disability, flat facies, epicanthal folds, Brushfield spots, single palmar crease, AV septal defects, duodenal atresia, and increased risk of ALL and Alzheimer's.",
    category: "Genetics",
  },
  {
    fact: "Edwards syndrome (trisomy 18) features: severe intellectual disability, rocker-bottom feet, overlapping fingers, micrognathia, and VSD. It has a very poor prognosis.",
    category: "Genetics",
  },
  {
    fact: "Patau syndrome (trisomy 13) features: holoprosencephaly, cleft lip/palate, polydactyly, microphthalmia, and cardiac defects. Fatal within the first year.",
    category: "Genetics",
  },
  {
    fact: "Turner syndrome (45,X) affects females: short stature, streak gonads, primary amenorrhea, webbed neck, bicuspid aortic valve, and coarctation of the aorta. Associated with lymphedema at birth.",
    category: "Genetics",
  },
  {
    fact: "Klinefelter syndrome (47,XXY) affects males: tall stature, small testes (hyalinized tubules), gynecomastia, infertility, and increased risk of breast cancer and SLE.",
    category: "Genetics",
  },
];
